import {
  DeleteConfirmation,
  Divider, ErrorMessages, IconButton, IconButtonType, Page, Spinner
} from "@whitespace/whitespace-ui";
import {
  Link, useNavigate, useParams, useLocation
} from "react-router-dom";
import {
  Breadcrumb,
  Button, Card, Col, Container, Form, FormGroup, Row
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { useCallback, useEffect, useState } from "react";
import { ariaInvalidCheck } from "@whitespace/whitespace-utils";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import IEditAuthorisedMaintenanceRequestModel from "interfaces/Request/IEditAuthorisedMaintenanceRequestModel";
import authorisedMaintenanceSchema from "modules/schemas/authorisedMaintenanceSchema";

const EditAuthorisedMaintenance = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { hwrcSiteId, diaryDate, authorisedMaintenanceId } = useParams();
  const editDetails = { ...location.state as IEditAuthorisedMaintenanceRequestModel };
  const returnUrl = `${RoutePathsEnum.SiteDiaryLandingPage}${hwrcSiteId && diaryDate ? `/${hwrcSiteId}/${diaryDate}` : ""}`;
  const [showExceptions, setShowExceptions] = useState<boolean>(true);
  const [updateButtonDisabled, setUpdateButtonDisabled] = useState<boolean>(true);
  const [deleteButtonPressed, setDeleteButtonPressed] = useState<boolean>(false);

  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues,
    setValue
  } = useForm<IEditAuthorisedMaintenanceRequestModel>({
    resolver: yupResolver(authorisedMaintenanceSchema),
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const {
    isSending: updateAuthorisedMaintenanceIsSending,
    errors: updateAuthorisedMaintenanceErrors,
    sendRequest: updateAuthorisedMaintenanceSendRequest,
    statusCode: updateAuthorisedMaintenanceStatusCode,
    resetState: updateAuthorisedMaintenanceResetState
  } = useAmplifyREST();

  const {
    isSending: deleteAuthorisedMaintenanceIsSending,
    errors: deleteAuthorisedMaintenanceErrors,
    sendRequest: deleteAuthorisedMaintenanceSendRequest,
    statusCode: deleteAuthorisedMaintenanceStatusCode
  } = useAmplifyREST();

  const isSending = updateAuthorisedMaintenanceIsSending || deleteAuthorisedMaintenanceIsSending;
  const apiErrors = updateAuthorisedMaintenanceErrors || deleteAuthorisedMaintenanceErrors;

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  const handleUpdateButtonClick = (request : IEditAuthorisedMaintenanceRequestModel) => {
    updateAuthorisedMaintenanceResetState();
    updateAuthorisedMaintenanceSendRequest(encodeURI(`hwrcsitediary/authorisedmaintenance/${authorisedMaintenanceId}`), "PUT", request);
  };

  useEffect(() => {
    if (updateAuthorisedMaintenanceStatusCode === 200) {
      navigate(returnUrl);
    }
  }, [updateAuthorisedMaintenanceStatusCode]);

  const handleDeleteButtonClick = () => {
    setDeleteButtonPressed(false);
    deleteAuthorisedMaintenanceSendRequest(encodeURI(`hwrcsitediary/authorisedmaintenance/${authorisedMaintenanceId}`), "DELETE");
  };

  useEffect(() => {
    if (deleteAuthorisedMaintenanceStatusCode === 200) {
      navigate(returnUrl);
    }
  }, [deleteAuthorisedMaintenanceStatusCode]);

  useEffect(() => {
    if (deleteAuthorisedMaintenanceStatusCode === 401) {
      navigate(RoutePathsEnum.Unauthorised);
    }
  }, [deleteAuthorisedMaintenanceStatusCode]);

  useEffect(() => {
    if (updateAuthorisedMaintenanceStatusCode === 401) {
      navigate(RoutePathsEnum.Unauthorised);
    }
  }, [updateAuthorisedMaintenanceStatusCode]);

  const canFormBeSubmitted = () => {
    const mandatoryFieldsEntered = (getValues("atwPtwNumber").length > 0 && getValues("undertakenBy").length > 0);

    const valueHasChanged = getValues("undertakenBy") !== editDetails.undertakenBy
    || getValues("atwPtwNumber") !== editDetails.atwPtwNumber
    || getValues("details") !== editDetails.details;

    setUpdateButtonDisabled(!(mandatoryFieldsEntered && valueHasChanged));
  };

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      <Divider />
      <Card>
        <Card.Header>
          <h1>
            Edit Maintenance Entry
            <Spinner
              animation="grow"
              visible={isSending}
              spinnerText="Loading"
              variant="info"
            />
          </h1>
        </Card.Header>
        <Card.Body className="no-padding">
          <Form
            autoComplete="off"
            onSubmit={handleSubmit(handleUpdateButtonClick)}
          >
            <Row className="d-lg-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <Breadcrumb className="breadcrumb-edit-maintenance-entry left-margin-large no-wrap">
                  <Breadcrumb.Item
                    data-testid="breadcrumb-diary"
                    linkAs={Link}
                    linkProps={{ to: returnUrl }}
                    onClick={() => navigate(returnUrl)}
                  >
                    Diary
                  </Breadcrumb.Item>
                  <Breadcrumb.Item active data-testid="breadcrumb-edit-maintenance">
                    Edit Maintenance Entry
                  </Breadcrumb.Item>
                </Breadcrumb>
              </Col>
              <Col className="d-lg-flex justify-content-end">
                <IconButton
                  iconType={IconButtonType.Delete}
                  variant="danger"
                  className="right-margin-small"
                  aria-disabled={deleteAuthorisedMaintenanceIsSending}
                  aria-label="Delete maintenance entry"
                  type="button"
                  spin={deleteAuthorisedMaintenanceIsSending}
                  onClick={() => setDeleteButtonPressed(true)}
                  data-testid="btnDelete"
                >
                  Delete
                </IconButton>
                <IconButton
                  iconType={IconButtonType.Save}
                  variant="primary"
                  className="right-margin-small"
                  disabled={updateButtonDisabled || updateAuthorisedMaintenanceIsSending}
                  aria-disabled={updateButtonDisabled || updateAuthorisedMaintenanceIsSending}
                  type="submit"
                  spin={updateAuthorisedMaintenanceIsSending}
                  data-testid="btnUpdate"
                  aria-label="Update maintenance entry"
                >
                  Update
                </IconButton>
                <Button
                  variant="secondary"
                  onClick={() => navigate(returnUrl)}
                  aria-label="Cancel update and return to site diary"
                  data-testid="cancel"
                >
                  Cancel
                </Button>
              </Col>
            </Row>

            <Divider />
            <section className="authorised-maintenance pt-2">
              <Container fluid>
                <Row>
                  <div className="control-pane">
                    <div className="control-section" />

                    <Row className="mt-3">
                      <FormGroup as={Row} controlId="undertakenBy">
                        <Form.Label id="undertakenByLabel" className="horiz-form-label">
                          Undertaken By:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required="true"
                            aria-labelledby="undertakenByLabel"
                            aria-invalid={ariaInvalidCheck(
                              "undertakenBy",
                              errors,
                              null
                            )}
                            maxLength={50}
                            minLength={1}
                            onKeyUp={() => canFormBeSubmitted()}
                            defaultValue={editDetails.undertakenBy}
                            {...register("undertakenBy")}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="undertakenBy"
                          errors={errors}
                          validationFailures={null}
                        />
                      </FormGroup>
                    </Row>
                    <Row className="mt-3">
                      <FormGroup as={Row} controlId="atwPtwNumber">
                        <Form.Label id="atwPtwNumberLabel" className="horiz-form-label">
                          ATW/PTW Number:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required="true"
                            aria-labelledby="atwPtwNumberLabel"
                            aria-invalid={ariaInvalidCheck(
                              "atwPtwNumber",
                              errors,
                              null
                            )}
                            maxLength={50}
                            minLength={1}
                            onKeyUp={() => canFormBeSubmitted()}
                            defaultValue={editDetails.atwPtwNumber}
                            {...register("atwPtwNumber")}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="atwPtwNumber"
                          errors={errors}
                          validationFailures={null}
                        />
                      </FormGroup>
                    </Row>
                    <Row className="mt-3 mb-3">
                      <FormGroup as={Row} controlId="details">
                        <Form.Label id="detailsLabel" className="horiz-form-label">
                          Details:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required="false"
                            aria-labelledby="detailsLabel"
                            aria-invalid={ariaInvalidCheck(
                              "details",
                              errors,
                              null
                            )}
                            maxLength={50}
                            minLength={1}
                            onKeyUp={() => canFormBeSubmitted()}
                            defaultValue={editDetails.details}
                            {...register("details")}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="details"
                          errors={errors}
                          validationFailures={null}
                        />
                      </FormGroup>
                    </Row>

                  </div>
                </Row>
              </Container>

            </section>

          </Form>
        </Card.Body>
      </Card>
      <DeleteConfirmation
        title="Delete Maintenance Entry"
        onDelete={() => { handleDeleteButtonClick(); }}
        onHide={() => { setDeleteButtonPressed(false); }}
        isDeleting={deleteAuthorisedMaintenanceIsSending}
        show={deleteButtonPressed}

      >
        <p>Are you sure you want to delete this maintenance entry?</p>
      </DeleteConfirmation>
    </Page>
  );
};

export default EditAuthorisedMaintenance;
